/**
 * NextStepBanner — the one next-step hook at the top of every graduated screen.
 * One grammar, four levels, always the same three lines:
 *
 *   NEXT AT PORTFOLIO · REVIEW OVERLAP
 *   2 new groups of projects working the same ground — review them before
 *   they are funded twice.                                 [Open Overlap →]
 *   from your overlap scan · computed over everything you're entitled to see
 *
 * The eyebrow says where you are and what the verb is; the sentence names the
 * subject in plain words; the ruler underneath says where the step came from
 * and what it does NOT change. The call to action is amber on every figure —
 * one colour for "the thing to do next", so a reader learns it once.
 *
 * Real data only: the page derives the step from live state (the queue, the
 * pipeline, the arrangements). When nothing is waiting, pass `none` with an
 * honest line rather than inventing a step.
 */
import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'

export interface NextStepBannerProps {
  /** Where the step sits, e.g. 'PORTFOLIO', 'MY INVENTIONS', 'Invention · Claims lens'. */
  at: string
  /** The verb, e.g. 'REVIEW OVERLAP', 'ACKNOWLEDGE', 'CONFIRM LOCK'. */
  verb: string
  headline: ReactNode
  /** The ruler under the headline: where the step comes from and what it changes. */
  rule?: ReactNode
  cta?: { label: string; to?: string; onClick?: () => void }
  /** The figures use the amber call to action everywhere; `primary` is the
   * blue alternative for a step that is not the page's main move. */
  tone?: 'amber' | 'primary'
  /** Nothing waiting: render the honest empty form instead. */
  none?: boolean
  /** Optional escape controls under the call to action — figures S4/S4b's
   * "Snooze ▾ · Not now". Re-applied by the MINE lane on top of the SPINE
   * lane's banner after merging main; omit it and the banner renders exactly
   * as it did before. */
  escape?: ReactNode
  /** Optional full-width row under a dashed rule — figures S4/S4b's
   * "2 more items in your queue — See queue · 1 snoozed · show". */
  queue?: ReactNode
  /** The glyph in the square. Each figure names the KIND of step in one
   * character — a shield for "protect", a flag for "read this", a tick for
   * "approve" — so a reader sees what sort of thing is waiting before reading
   * the sentence. Optional and additive: the default is the arrow every screen
   * shipped with, so a page that says nothing is unchanged.
   * (Added by the INV lane for S1/S2/S3; flagged to the SPINE lane.) */
  icon?: ReactNode
}

export default function NextStepBanner({
  at,
  verb,
  headline,
  rule,
  cta,
  tone = 'amber',
  none = false,
  escape,
  queue,
  icon,
}: NextStepBannerProps) {
  const btn =
    tone === 'primary'
      ? 'bg-iflow-500 text-white hover:bg-iflow-600'
      : 'bg-amber-400 text-gray-900 hover:bg-amber-300'
  return (
    <section
      className="mb-[18px] flex flex-wrap items-start gap-3.5 rounded-xl border border-iflow-200 border-l-4 border-l-iflow-500 bg-gradient-to-r from-iflow-50 to-white px-4 py-[13px]"
      data-testid="next-step-banner"
      aria-label={none ? `Nothing waiting at ${at}` : `Next at ${at}`}
    >
      <span
        className="mt-px flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] bg-iflow-100 text-[17px] leading-none text-iflow-700"
        aria-hidden
      >
        {icon ?? '➜'}
      </span>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 text-[10.5px] font-extrabold uppercase tracking-[0.7px] text-iflow-600">
          {none ? `Nothing waiting at ${at}` : `Next at ${at} · ${verb}`}
        </div>
        <div className="text-[13.5px] font-bold text-iflow-900">{headline}</div>
        {rule ? <div className="mt-[3px] text-[11px] italic text-slate-500">{rule}</div> : null}
      </div>
      {(cta || escape) && !none ? (
        <div className="flex shrink-0 flex-col items-end gap-1 self-center">
          {cta ? (
            cta.to ? (
              <Link to={cta.to} className={`rounded-[9px] px-[11px] py-1.5 text-[12px] font-semibold ${btn}`}>
                {cta.label}
              </Link>
            ) : (
              <button
                type="button"
                onClick={cta.onClick}
                className={`rounded-[9px] px-[11px] py-1.5 text-[12px] font-semibold ${btn}`}
              >
                {cta.label}
              </button>
            )
          ) : null}
          {escape}
        </div>
      ) : null}
      {queue ? (
        <div className="mt-0.5 basis-full border-t border-dashed border-iflow-200 pt-[7px] text-[11.5px] text-slate-600">
          {queue}
        </div>
      ) : null}
    </section>
  )
}
